import React, { useEffect, useState } from "react";
import axios from "axios";
import { MessageSquare, Loader2 } from "lucide-react";
import { BulkFirmSms } from "./BulkFirmSms";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RANGES = [["7", "7 gün"], ["30", "30 gün"], ["90", "90 gün"], ["0", "Tümü"]];
const fmt = (n) => (n || 0).toLocaleString("tr-TR");

const Stat = ({ label, value, hint, testid }) => (
  <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5" data-testid={testid}>
    <p className="text-xs text-[#7C6E70]">{label}</p>
    <p className="text-3xl font-bold text-[#2A2224] mt-1">{fmt(value)}</p>
    {hint && <p className="text-xs text-[#a8999b] mt-1">{hint}</p>}
  </div>
);

export const SmsReport = ({ tab, headers, settings }) => {
  if (tab === "sms-toplu") return <BulkFirmSms headers={headers} />;
  if (tab === "sms-ayarlar") return <div className="space-y-5">{settings}</div>;
  return <SmsReportView headers={headers} />;
};

const SmsReportView = ({ headers }) => {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/admin/sms-report`, { headers, params: { days } }).then((r) => setData(r.data)).catch(() => setData({ total: 0, totalParts: 0, byKind: [], byMember: [] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const max = Math.max(1, ...(data?.byKind || []).map((k) => k.count));

  return (
    <div className="space-y-5" data-testid="sms-report">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display text-2xl text-[#2A2224] flex items-center gap-2"><MessageSquare className="w-5 h-5 text-[#9E2A3B]" /> SMS Raporu</h2>
        <div className="flex gap-1.5 bg-white rounded-full border border-[#F0DDE0] p-1">
          {RANGES.map(([v, l]) => (
            <button key={v} onClick={() => setDays(v)} data-testid={`sms-range-${v}`}
              className={`px-3.5 py-1.5 rounded-full text-xs font-medium transition-colors ${days === v ? "bg-[#9E2A3B] text-white" : "text-[#7C6E70] hover:text-[#9E2A3B]"}`}>{l}</button>
          ))}
        </div>
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-[#C15B6B]" /></div>
      ) : (
        <>
          <div className="grid sm:grid-cols-2 gap-4">
            <Stat label="Gönderilen SMS (alıcı sayısı)" value={data.total} testid="sms-total" />
            <Stat label="Netgsm kredisi (tahmini)" value={data.totalParts} hint="Uzun mesajlar birden fazla SMS sayılır" testid="sms-total-parts" />
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] p-5" data-testid="sms-by-kind">
            <p className="text-sm font-medium text-[#2A2224] mb-4">Türüne Göre</p>
            {!data.byKind.length ? <p className="text-sm text-[#a8999b] py-6 text-center">Bu dönemde SMS gönderilmedi.</p> : (
              <ul className="space-y-3">
                {data.byKind.map((k) => (
                  <li key={k.kind} data-testid={`sms-kind-${k.kind}`}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-[#2A2224]">{k.label}</span>
                      <span className="font-medium text-[#9E2A3B]">{fmt(k.count)} <span className="text-[#a8999b] font-normal">· {fmt(k.parts)} kredi</span></span>
                    </div>
                    <div className="h-2 rounded-full bg-[#FBEEF0] overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#C15B6B] to-[#9E2A3B]" style={{ width: `${(k.count / max) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="bg-white rounded-2xl border border-[#F0DDE0] overflow-hidden" data-testid="sms-by-member">
            <p className="text-sm font-medium text-[#2A2224] p-5 pb-3">Üyelere Göre Davetiye SMS'leri</p>
            {!data.byMember.length ? <p className="text-sm text-[#a8999b] pb-8 text-center">Bu dönemde davetiye SMS'i gönderilmedi.</p> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#FBF5F0] text-[#7C6E70] text-xs">
                    <tr><th className="text-left px-5 py-2.5 font-medium">Üye</th><th className="text-left px-5 py-2.5 font-medium">Telefon</th><th className="text-left px-5 py-2.5 font-medium">Şehir</th><th className="text-right px-5 py-2.5 font-medium">SMS</th><th className="text-right px-5 py-2.5 font-medium">Son gönderim</th></tr>
                  </thead>
                  <tbody>
                    {data.byMember.map((m) => (
                      <tr key={m.memberId} className="border-t border-[#F6ECEE]" data-testid={`sms-member-row-${m.memberId}`}>
                        <td className="px-5 py-3 text-[#2A2224]">{m.name}</td>
                        <td className="px-5 py-3 text-[#7C6E70]">{m.phone}</td>
                        <td className="px-5 py-3 text-[#7C6E70]">{m.city || "-"}</td>
                        <td className="px-5 py-3 text-right font-semibold text-[#9E2A3B]">{fmt(m.count)}</td>
                        <td className="px-5 py-3 text-right text-[#7C6E70] whitespace-nowrap">{new Date(m.last).toLocaleString("tr-TR", { dateStyle: "short", timeStyle: "short" })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};
